import { del, get, searchAllPages, post, queryBody } from '@/lib/apiClient';
import { endpoints } from './endpoints';
import { pagedList } from './pagedList';
import { schoolScope } from './schoolScope';
import type {
  FoundationHostelBlock,
  FoundationTransportRoute,
  StaffActivationCheck,
  StaffAssignment,
  StaffCompleteness,
  StaffAssignmentInput,
  StaffAssignmentRolloverResult,
  StaffDetails,
  StaffDetailsInput,
  StaffFieldPolicy,
  StaffMe,
  StaffExperience,
  StaffMember,
  StaffPersonalInput,
  StaffProfile,
  StaffQualification,
  StaffStatusChangeInput,
  StaffStatusHistoryEntry,
} from '@/models';

const f = endpoints.foundation;

const fromApiCode = (value: string | null | undefined) =>
  value ? value.toLowerCase().replaceAll('_', '') : null;

const toStaffMember = (staff: StaffMember): StaffMember => ({
  ...staff,
  gender: fromApiCode(staff.gender),
  zip_Code: staff.zip_Code ? Number(staff.zip_Code) : 0,
});

const toStaffInput = (staff: StaffPersonalInput): StaffPersonalInput => ({
  ...staff,
  gender: staff.gender ? staff.gender.toUpperCase() : staff.gender,
  zip_Code: staff.zip_Code == null ? undefined : String(staff.zip_Code),
});

export const listStaff = async (schoolId: number, signal?: AbortSignal) =>
  (await searchAllPages<StaffMember>(f.staff(), {}, { ...(await schoolScope(schoolId)), signal })).map(toStaffMember);

export const deleteStaff = async (schoolId: number, staffId: number) =>
  del<unknown>(f.staffMember(staffId), await schoolScope(schoolId));

/** Whether another staff member already has this email, mobile, Aadhaar number or staff code. */
export const checkStaffDuplicate = async (
  schoolId: number,
  field: 'email' | 'mobile' | 'aadhaar' | 'staffcode',
  value: string,
  exceptStaffId?: number,
  signal?: AbortSignal,
) =>
  post<{ duplicate: boolean }>(f.staff() + '/duplicate-check', queryBody({ field, value, exceptStaffId }), {
    ...(await schoolScope(schoolId)),
    signal,
  });

export const getStaffDetails = async (
  schoolId: number,
  staffId: number,
  signal?: AbortSignal,
): Promise<StaffDetails> => {
  const config = { ...(await schoolScope(schoolId)), signal };
  const [qualifications, experiences] = await Promise.all([
    searchAllPages<StaffQualification>(f.staffQualifications(staffId), {}, config),
    searchAllPages<StaffExperience>(f.staffExperiences(staffId), {}, config),
  ]);
  return { qualifications, experiences };
};

/**
 * Creates (no `staffId`) or updates a staff member together with their
 * qualifications and experiences, in one transaction: rows with an id are
 * updated, rows without one created, and stored rows left out are deleted.
 * A failure saves nothing.
 */
export const saveStaffProfile = async (
  schoolId: number,
  staffId: number | undefined,
  staff: StaffPersonalInput,
  details: StaffDetailsInput,
): Promise<StaffProfile> => {
  const body = { staff: toStaffInput(staff), ...details };
  const scope = await schoolScope(schoolId);
  const profile = staffId
    ? await post<StaffProfile>(f.staffProfile(staffId), body, scope)
    : await post<StaffProfile>(f.staffProfiles(), body, scope);
  return { ...profile, staff: toStaffMember(profile.staff) };
};

/**
 * One row of the bulk staff import: creates the staff member with their qualifications and
 * experiences, ACTIVE at once. Only the import skips the activation checks; the documents and
 * other activation details are completed afterwards from the staff record.
 */
export const importStaffProfile = async (
  schoolId: number,
  staff: StaffPersonalInput,
  details: StaffDetailsInput,
): Promise<StaffProfile> => {
  const profile = await post<StaffProfile>(
    f.staffImport(),
    { staff: toStaffInput(staff), ...details },
    await schoolScope(schoolId),
  );
  return { ...profile, staff: toStaffMember(profile.staff) };
};

/** Which fields a staff type / designation needs (the staff field policy). */
export const getStaffFieldPolicy = async (
  schoolId: number,
  params: { staffType?: string; designation?: string; employmentType?: string },
) => {
  // Empty values are left out, so the API falls back to its defaults.
  const query = Object.fromEntries(Object.entries(params).filter(([, v]) => Boolean(v)));
  return post<StaffFieldPolicy>(f.staffFieldPolicy(), query, await schoolScope(schoolId));
};

/** The staff member with every record the wizard edits (qualifications, experiences, credentials, subjects). */
export const getStaffProfile = async (schoolId: number, staffId: number): Promise<StaffProfile> => {
  const profile = await get<StaffProfile>(f.staffProfile(staffId), await schoolScope(schoolId));
  return { ...profile, staff: toStaffMember(profile.staff) };
};

/** What the staff member still needs before activation. */
export const getActivationCheck = async (schoolId: number, staffId: number) =>
  get<StaffActivationCheck>(f.staffLifecycle(staffId, 'activation-check'), await schoolScope(schoolId));

export type StaffStatusAction = 'activate' | 'leave' | 'relieve' | 'reactivate';

/** Moves the staff member through the lifecycle (DRAFT → ACTIVE ⇄ ON_LEAVE → RELIEVED → ACTIVE). */
export const changeStaffStatus = async (
  schoolId: number,
  staffId: number,
  action: StaffStatusAction,
  input: StaffStatusChangeInput = {},
) => toStaffMember(await post<StaffMember>(f.staffLifecycle(staffId, action), input, await schoolScope(schoolId)));

export const listStaffStatusHistory = async (schoolId: number, staffId: number) =>
  get<StaffStatusHistoryEntry[]>(f.staffLifecycle(staffId, 'status-history'), await schoolScope(schoolId));

// --- Assignments (class teacher, subject teacher, head of department) ---------

export const listStaffAssignments = async (schoolId: number, staffId: number) =>
  get<StaffAssignment[]>(f.staffAssignments(staffId), await schoolScope(schoolId));

export const saveStaffAssignment = async (
  schoolId: number,
  staffId: number,
  assignmentId: number | undefined,
  input: StaffAssignmentInput,
) =>
  assignmentId
    ? post<StaffAssignment>(f.staffAssignment(staffId, assignmentId), input, await schoolScope(schoolId))
    : post<StaffAssignment>(f.staffAssignments(staffId), input, await schoolScope(schoolId));

/** Ends the assignment on `endDate` (today when null). */
export const endStaffAssignment = async (schoolId: number, staffId: number, assignmentId: number, endDate: string | null) =>
  post<StaffAssignment>(`${f.staffAssignment(staffId, assignmentId)}/end`, { endDate }, await schoolScope(schoolId));

export const deleteStaffAssignment = async (schoolId: number, staffId: number, assignmentId: number) =>
  del<void>(f.staffAssignment(staffId, assignmentId), await schoolScope(schoolId));

/** Copies one academic year's assignments into a later year. */
export const rolloverStaffAssignments = async (schoolId: number, fromAcademicYearId: number, toAcademicYearId: number) =>
  post<StaffAssignmentRolloverResult>(
    f.staffAssignmentRollover(),
    { fromAcademicYearId, toAcademicYearId },
    await schoolScope(schoolId),
  );

// --- Platform login -------------------------------------------------------------

/** Retries creating the platform login of an active staff member whose login is still requested. */
export const createStaffLogin = async (schoolId: number, staffId: number) =>
  toStaffMember(await post<StaffMember>(f.staffLifecycle(staffId, 'login'), {}, await schoolScope(schoolId)));

/** The signed-in user's own staff record and current assignments (a teacher's scope). */
export const getMyStaff = async (schoolId: number): Promise<StaffMe> => {
  const me = await get<StaffMe>(`${f.staff()}/me`, await schoolScope(schoolId));
  return { ...me, staff: toStaffMember(me.staff) };
};

/** Profile completeness of every staff member of the school, with what each one is missing. */
export const listStaffCompleteness = async (schoolId: number) =>
  get<StaffCompleteness[]>(`${f.staff()}/completeness`, await schoolScope(schoolId));

/** Hostel blocks of the school (Foundation API), for warden assignments. */
export const listHostelBlockScopes = async (schoolId: number) =>
  searchAllPages<FoundationHostelBlock>(f.hostelBlocks(), {}, await schoolScope(schoolId));

/** Bus routes of the school (Foundation API), for route in-charge assignments. */
export const listTransportRouteScopes = async (schoolId: number) =>
  searchAllPages<FoundationTransportRoute>(f.transportRoutes(), {}, await schoolScope(schoolId));

// Server-driven table lists.

/** One directory page: search, toolbar filters, sorting and the relieved/profile switches run in the API. */
export const listStaffPage = pagedList<StaffMember>(f.staff(), toStaffMember);
